/**
 * CloudCodeView Component
 * 
 * VS Code-like interface for viewing cloud code files.
 * Features:
 * - Toggle between Files view and Functions view
 * - File tree sidebar for navigation
 * - Monaco Editor for code viewing
 * - Functions list with test console
 * - Syntax highlighting for JS, JSON, HTML, CSS
 * 
 * Layout (Files View):
 * ┌─────────────────┬────────────────────────────────────┐
 * │  File Tree      │  Monaco Editor                     │
 * │  (sidebar)      │  (main content)                    │
 * └─────────────────┴────────────────────────────────────┘
 * 
 * Layout (Functions View):
 * ┌──────────────────────────────────────────────────────┐
 * │  Function Cards with Test Buttons                    │
 * │  Triggers grouped by class                           │
 * └──────────────────────────────────────────────────────┘
 */

import { useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import Editor from '@monaco-editor/react';
import { CloudTreeNode, ExtractedFile, CloudFunction, Trigger, CloudCodeItem } from '../../../../types/AppPanel';
import FileTree from './FileTree';
import CodeEditor, { getLanguageFromExtension } from './CodeEditor';
import FunctionsView from './FunctionsView';

interface CloudCodeViewProps {
  /** Raw tree from useCloudTree */
  tree?: CloudTreeNode[];
  /** Processed cloud files */
  cloudFiles?: ExtractedFile[];
  /** List of cloud functions (for summary) */
  functions?: CloudFunction[];
  /** List of triggers (for summary) */
  triggers?: Trigger[];
  /** Unified list of cloud code items */
  cloudCodeItems?: CloudCodeItem[];
  /** Whether data is loading */
  isLoading?: boolean;
  /** URL to open full cloud code editor */
  dashboardUrl?: string;
  /** Parse Server URL for testing functions */
  parseServerURL?: string;
  /** Application ID for testing functions */
  appId?: string;
  /** Master Key for testing functions */
  masterKey?: string;
  /** Current sub-view (controlled from parent) */
  subView?: 'files' | 'functions';
}

/**
 * Decode base64 file content from CloudTreeNode
 * Format: "data:plain/text;base64,<content>"
 */
function decodeFileContent(node: CloudTreeNode): string {
  if (!node.data?.code) return '// No content available';
  
  const code = node.data.code;
  
  // Handle base64 encoded content
  const base64Match = code.match(/base64,(.+)/);
  if (base64Match) {
    try {
      return atob(base64Match[1]);
    } catch {
      return '// Error decoding file content';
    }
  }
  
  // Return as-is if not base64
  return code;
}

export default function CloudCodeView({
  tree = [],
  cloudFiles = [],
  functions = [],
  triggers = [],
  cloudCodeItems = [],
  isLoading = false,
  dashboardUrl,
  parseServerURL,
  appId,
  masterKey,
  subView = 'files',
}: CloudCodeViewProps) {
  // Selected file state (for files view)
  const [selectedNode, setSelectedNode] = useState<CloudTreeNode | null>(null);
  const [selectedPath, setSelectedPath] = useState<string | undefined>();
  const [isDecodingFile, setIsDecodingFile] = useState(false);
  const [fileContent, setFileContent] = useState<string>('');
  // Fullscreen modal state
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Handle file selection
  const handleFileSelect = useCallback((node: CloudTreeNode, path: string) => {
    setSelectedNode(node);
    setSelectedPath(path);
    setIsDecodingFile(true);
    
    // Decode content (async-like to show loading)
    setTimeout(() => {
      const content = decodeFileContent(node);
      setFileContent(content);
      setIsDecodingFile(false);
    }, 50);
  }, []);

  // Get language for selected file
  const language = useMemo(() => {
    if (!selectedNode) return 'plaintext';
    return getLanguageFromExtension(selectedNode.text);
  }, [selectedNode]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="flex items-center gap-3 text-white/50">
          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span>Loading cloud code...</span>
        </div>
      </div>
    );
  }

  // Empty state - no cloud folder in tree
  const hasCloudFolder = tree.some(n => n.text === 'cloud');
  if (!hasCloudFolder) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 gap-4">
        <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center">
          <CodeIcon className="text-white/30" />
        </div>
        <p className="text-white/40 text-sm text-center">
          No cloud code deployed yet.<br />
          Deploy cloud functions to see them here.
        </p>
        {dashboardUrl && (
          <a
            href={`${dashboardUrl}/cloud-code`}
            target="_blank"
            rel="noreferrer noopener"
            className="mt-2 px-4 py-2 rounded-lg bg-[#208AEC] text-white text-sm hover:bg-[#208AEC]/80 transition-colors"
          >
            Open Cloud Code Editor
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Content based on sub-view */}
      {subView === 'files' ? (
        /* Files View: File tree + Editor */
        <div className="flex-1 flex overflow-hidden">
          {/* File Tree Sidebar */}
          <div className="w-56 flex-shrink-0 border-r border-white/5 bg-black/20 flex flex-col">
            {/* Sidebar header */}
            <div className="px-3 py-2 text-xs font-semibold text-white/40 uppercase tracking-wider border-b border-white/5">
              Explorer
            </div>
            
            {/* Tree */}
            <FileTree
              tree={tree}
              selectedPath={selectedPath}
              onFileSelect={handleFileSelect}
              rootFilter={['cloud']}
            />
          </div>

          {/* Editor Area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {selectedNode ? (
              <>
                {/* File tab / breadcrumb */}
                <div className="flex items-center gap-2 px-4 py-2 bg-[#252526] border-b border-[#3c3c3c]">
                  <FileTabIcon filename={selectedNode.text} />
                  <span className="text-sm text-white/80">{selectedNode.text}</span>
                  <span className="text-xs text-white/40 ml-auto font-mono">
                    {selectedPath}
                  </span>
                  {/* Fullscreen Toggle Button */}
                  <button
                    onClick={() => setIsFullscreen(true)}
                    className="ml-2 p-1.5 rounded hover:bg-white/10 text-white/50 hover:text-white transition-colors"
                    title="Expand to fullscreen"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"/>
                    </svg>
                  </button>
                </div>
                
                {/* Monaco Editor */}
                <CodeEditor
                  code={fileContent}
                  language={language}
                  isLoading={isDecodingFile}
                  readOnly
                />
              </>
            ) : (
              /* No file selected */
              <div className="flex-1 flex flex-col items-center justify-center bg-[#1e1e1e] text-white/40">
                <CodeIcon className="w-12 h-12 mb-4 opacity-30" />
                <p className="text-sm">Select a file to view its contents</p>
                <p className="text-xs mt-2 text-white/30">
                  Use the explorer on the left to navigate
                </p>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Functions View */
        <FunctionsView
          items={cloudCodeItems}
          parseServerURL={parseServerURL}
          appId={appId}
          masterKey={masterKey}
        />
      )}

      {/* Fullscreen Modal - rendered outside the ternary via Portal */}
      {isFullscreen && selectedNode && createPortal(
        <div className="fixed inset-0 z-[9999] flex flex-col bg-[#1e1e1e]">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-[#252526] border-b border-[#3c3c3c]">
            <div className="flex items-center gap-3 min-w-0">
              {/* File Icon */}
              <FileTabIcon filename={selectedNode.text} />
              
              {/* Filename */}
              <span className="text-sm font-medium text-white">{selectedNode.text}</span>
              
              {/* Path */}
              <span className="text-xs text-white/40 font-mono">
                {selectedPath}
              </span>
            </div>

            <div className="flex items-center gap-2">
              {/* Copy Button */}
              <button
                onClick={() => {
                  navigator.clipboard.writeText(fileContent);
                }}
                className="flex items-center gap-1.5 px-2 py-1 rounded text-xs text-white/60 hover:text-white hover:bg-white/10 transition-colors"
                title="Copy content"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                  <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                </svg>
                Copy
              </button>

              {/* Exit Fullscreen Button */}
              <button
                onClick={() => setIsFullscreen(false)}
                className="p-1.5 rounded hover:bg-white/10 text-white/50 hover:text-white transition-colors"
                title="Exit fullscreen"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3"/>
                </svg>
              </button>

              {/* Close Button */}
              <button
                onClick={() => setIsFullscreen(false)}
                className="p-1.5 rounded hover:bg-white/10 text-white/50 hover:text-white transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Editor */}
          <div className="flex-1 overflow-hidden">
            <Editor
              height="calc(100vh - 52px)"
              language={language}
              value={fileContent}
              theme="vs-dark"
              options={{
                readOnly: true,
                minimap: { enabled: true },
                fontSize: 13,
                fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
                lineNumbers: 'on',
                scrollBeyondLastLine: false,
                automaticLayout: true,
                padding: { top: 16, bottom: 16 },
                scrollbar: { verticalScrollbarSize: 10, horizontalScrollbarSize: 10 },
                wordWrap: 'on',
                folding: true,
                renderLineHighlight: 'line',
              }}
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ============================================================================
// Icons
// ============================================================================

function CodeIcon({ className = '' }: { className?: string }) {
  return (
    <svg 
      width="32" 
      height="32" 
      viewBox="0 0 24 24" 
      fill="none" 
      className={className}
    >
      <path 
        d="M8 9L4 13L8 17" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
      <path 
        d="M16 9L20 13L16 17" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
      <path 
        d="M14 5L10 19" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round"
      />
    </svg>
  );
}

function FileTabIcon({ filename }: { filename: string }) {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  
  const colors: Record<string, string> = {
    js: 'text-yellow-400',
    mjs: 'text-yellow-400',
    ts: 'text-blue-400',
    json: 'text-yellow-600',
    html: 'text-orange-400',
    css: 'text-blue-500',
  };
  
  return (
    <svg 
      width="14" 
      height="14" 
      viewBox="0 0 24 24" 
      fill="none" 
      className={colors[ext] || 'text-white/50'}
    >
      <path 
        d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" 
        stroke="currentColor" 
        strokeWidth="2"
      />
      <path d="M14 2v6h6" stroke="currentColor" strokeWidth="2"/>
    </svg>
  );
}
